// (c) 2026 Planet Q Productions. All Rights Reserved.

// MusicEngine.js — Web Audio API synthesizer for procedural video game music

const NOTE_FREQUENCIES = {
  'C2': 65.41, 'D2': 73.42, 'E2': 82.41, 'F2': 87.31, 'G2': 98.00, 'A2': 110.00, 'B2': 123.47,
  'C3': 130.81, 'D3': 146.83, 'E3': 164.81, 'F3': 174.61, 'G3': 196.00, 'A3': 220.00, 'B3': 246.94,
  'C4': 261.63, 'D4': 293.66, 'E4': 329.63, 'F4': 349.23, 'G4': 392.00, 'A4': 440.00, 'B4': 493.88,
  'C5': 523.25, 'D5': 587.33, 'E5': 659.25, 'F5': 698.46, 'G5': 783.99, 'A5': 880.00, 'B5': 987.77,
  'C#2': 69.30, 'D#2': 77.78, 'F#2': 92.50, 'G#2': 103.83, 'A#2': 116.54,
  'C#3': 138.59, 'D#3': 155.56, 'F#3': 185.00, 'G#3': 207.65, 'A#3': 233.08,
  'C#4': 277.18, 'D#4': 311.13, 'F#4': 369.99, 'G#4': 415.30, 'A#4': 466.16,
  'C#5': 554.37, 'D#5': 622.25, 'F#5': 739.99, 'G#5': 830.61, 'A#5': 932.33,
  'Db2': 69.30, 'Eb2': 77.78, 'Gb2': 92.50, 'Ab2': 103.83, 'Bb2': 116.54,
  'Db3': 138.59, 'Eb3': 155.56, 'Gb3': 185.00, 'Ab3': 207.65, 'Bb3': 233.08,
  'Db4': 277.18, 'Eb4': 311.13, 'Gb4': 369.99, 'Ab4': 415.30, 'Bb4': 466.16,
  'Db5': 554.37, 'Eb5': 622.25, 'Gb5': 739.99, 'Ab5': 830.61, 'Bb5': 932.33,
};

class MusicEngine {
  constructor() {
    this.audioContext = null;
    this.masterGain = null;
    this.isPlaying = false;
    this.scheduledNodes = [];
    this.loopTimeout = null;
    this.musicData = null;
  }

  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.masterGain = this.audioContext.createGain();
      this.masterGain.gain.value = 0.5;
      this.masterGain.connect(this.audioContext.destination);
    }
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
  }

  setVolume(vol) {
    if (this.masterGain) {
      this.masterGain.gain.value = Math.max(0, Math.min(1, vol));
    }
  }

  playNote(frequency, waveType, volume, startTime, duration) {
    if (!this.audioContext || !frequency) return;

    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();

    osc.type = waveType || 'square';
    osc.frequency.value = frequency;

    // Envelope: attack, sustain, release
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(volume, startTime + 0.01);
    gain.gain.setValueAtTime(volume, startTime + duration * 0.7);
    gain.gain.linearRampToValueAtTime(0, startTime + duration);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(startTime);
    osc.stop(startTime + duration + 0.05);

    this.scheduledNodes.push(osc);
  }

  playDrum(type, startTime, volume) {
    if (!this.audioContext) return;

    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    const dur = type === 'hihat' ? 0.05 : 0.1;

    if (type === 'kick') {
      osc.frequency.setValueAtTime(150, startTime);
      osc.frequency.exponentialRampToValueAtTime(30, startTime + 0.1);
      osc.type = 'sine';
    } else if (type === 'snare') {
      osc.frequency.value = 200;
      osc.type = 'triangle';
    } else {
      osc.frequency.value = 800;
      osc.type = 'square';
    }

    gain.gain.setValueAtTime(volume || 0.3, startTime);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + dur);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(startTime);
    osc.stop(startTime + dur + 0.01);

    // Noise for snare/hihat
    if (type === 'snare' || type === 'hihat') {
      const bufferSize = this.audioContext.sampleRate * dur;
      const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * (type === 'hihat' ? 0.3 : 0.5);
      }
      const noise = this.audioContext.createBufferSource();
      noise.buffer = buffer;
      const noiseGain = this.audioContext.createGain();
      noiseGain.gain.setValueAtTime((volume || 0.3) * 0.5, startTime);
      noiseGain.gain.exponentialRampToValueAtTime(0.001, startTime + dur);
      noise.connect(noiseGain);
      noiseGain.connect(this.masterGain);
      noise.start(startTime);
      noise.stop(startTime + dur + 0.01);
      this.scheduledNodes.push(noise);
    }

    this.scheduledNodes.push(osc);
  }

  scheduleLoop() {
    if (!this.musicData || !this.isPlaying) return;

    const { tempo, tracks } = this.musicData;
    const beatDuration = 60 / (tempo || 120);
    const now = this.audioContext.currentTime + 0.1;

    let loopDuration = 0;

    // Melody
    if (tracks?.melody?.notes) {
      let time = now;
      for (const note of tracks.melody.notes) {
        const dur = (note.duration || 0.25) * beatDuration;
        if (note.note && note.note !== 'rest') {
          const freq = NOTE_FREQUENCIES[note.note];
          if (freq) {
            this.playNote(freq, tracks.melody.wave || 'square', tracks.melody.volume || 0.2, time, dur);
          }
        }
        time += dur;
      }
      loopDuration = Math.max(loopDuration, time - now);
    }

    // Bass
    if (tracks?.bass?.notes) {
      let time = now;
      for (const note of tracks.bass.notes) {
        const dur = (note.duration || 0.5) * beatDuration;
        if (note.note && note.note !== 'rest') {
          const freq = NOTE_FREQUENCIES[note.note];
          if (freq) {
            this.playNote(freq, tracks.bass.wave || 'sawtooth', tracks.bass.volume || 0.15, time, dur);
          }
        }
        time += dur;
      }
      loopDuration = Math.max(loopDuration, time - now);
    }

    // Arpeggio
    if (tracks?.arpeggio?.notes) {
      let time = now;
      for (const note of tracks.arpeggio.notes) {
        const dur = (note.duration || 0.125) * beatDuration;
        if (note.note && note.note !== 'rest') {
          const freq = NOTE_FREQUENCIES[note.note];
          if (freq) {
            this.playNote(freq, tracks.arpeggio.wave || 'triangle', tracks.arpeggio.volume || 0.1, time, dur);
          }
        }
        time += dur;
      }
      loopDuration = Math.max(loopDuration, time - now);
    }

    // Drums
    if (tracks?.drums?.pattern) {
      for (const hit of tracks.drums.pattern) {
        const time = now + (hit.time || 0) * beatDuration;
        this.playDrum(hit.type || 'kick', time, hit.volume || 0.3);
      }
      const lastHit = tracks.drums.pattern.reduce((max, h) => Math.max(max, h.time || 0), 0);
      loopDuration = Math.max(loopDuration, (lastHit + 1) * beatDuration);
    }

    // Loop continuously — schedule next loop when current one finishes
    if (loopDuration > 0 && this.isPlaying) {
      this.loopTimeout = setTimeout(() => {
        if (this.isPlaying && this.musicData) {
          this.scheduledNodes = [];
          this.scheduleLoop();
        }
      }, loopDuration * 1000);
    }
  }

  play(musicData) {
    this.init();
    this.stop();
    this.musicData = musicData;
    this.isPlaying = true;
    this.scheduleLoop();
  }

  stop() {
    this.isPlaying = false;
    if (this.loopTimeout) {
      clearTimeout(this.loopTimeout);
      this.loopTimeout = null;
    }
    this.scheduledNodes.forEach(node => {
      try { node.stop(); } catch (e) { /* already stopped */ }
    });
    this.scheduledNodes = [];
  }

  destroy() {
    this.stop();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

// Singleton instance
const musicEngine = new MusicEngine();
export default musicEngine;
